import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  Mail,
  Lock,
  Sparkles,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RoutesPaths } from "../../Router/constant";
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon } from "./onboarding/icons";
import OtpInput from "./OtpInput";
import {
  forgotPasswordAction,
  verifyResetOtpAction,
  resetPasswordAction,
} from "../../actions/auth";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;
const MIN_PASSWORD = 8;

const STEPS = [
  { id: "email", label: "Enter your email" },
  { id: "code", label: "Verify the code" },
  { id: "password", label: "Set a new password" },
];

export default function ForgotPassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [stepIndex, setStepIndex] = useState(0);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const [resent, setResent] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);

  const step = STEPS[stepIndex].id;

  useEffect(() => {
    if (step !== "code" || secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, secondsLeft]);

  const handleSendCode = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    if (!email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    setError("");
    setSubmitting(true);

    const result = await dispatch(
      forgotPasswordAction({ email: email.trim() }),
    );

    setSubmitting(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't send a reset code.");
      return;
    }

    setSecondsLeft(RESEND_SECONDS);
    setStepIndex(1);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (code.length !== OTP_LENGTH) {
      setError(`Please enter all ${OTP_LENGTH} digits.`);
      return;
    }

    setError("");
    setSubmitting(true);

    const result = await dispatch(
      verifyResetOtpAction({ email: email.trim(), code }),
    );

    setSubmitting(false);

    if (!result?.success) {
      setError(result?.message || "That code isn't right or has expired.");
      // A rejected code is worth clearing so the next attempt starts clean.
      setCode("");
      setResetSignal((n) => n + 1);
      return;
    }

    setResent(false);
    setStepIndex(2);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      setError("Please enter and confirm your new password.");
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError("Those passwords don't match.");
      return;
    }

    setError("");
    setSubmitting(true);

    // Step 2 already proved the code, so no OTP is sent here.
    const result = await dispatch(
      resetPasswordAction({ email: email.trim(), newPassword: password }),
    );

    setSubmitting(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't reset your password.");
      return;
    }

    // Back to sign-in, where the notice explains what just happened.
    navigate(RoutesPaths.LOGIN, {
      replace: true,
      state: { passwordReset: true, email: email.trim() },
    });
  };

  // Resending is the same call as step 1 — it mails a fresh code.
  const handleResend = async () => {
    if (secondsLeft > 0 || submitting) return;

    setError("");

    const result = await dispatch(
      forgotPasswordAction({ email: email.trim() }),
    );

    if (!result?.success) {
      setError(result?.message || "We couldn't send a new code.");
      return;
    }

    setCode("");
    setResetSignal((n) => n + 1);
    setResent(true);
    setSecondsLeft(RESEND_SECONDS);
  };

  const handleCodeChange = (value) => {
    setCode(value);
    setError("");
  };

  const goBackToEmail = () => {
    setStepIndex(0);
    setCode("");
    setResetSignal((n) => n + 1);
    setError("");
    setResent(false);
  };

  return (
    <main className="onboarding">
      <div className="auth-split">
        <div className="auth-split__brand auth-split__brand--light">
          <span className="auth-split__dots" aria-hidden="true" />

          <div className="auth-split__brand-inner">
          <div className="auth-brand-copy">
            <div className="auth-split__logoImg">
              <img src="/assets/logo.svg" alt="" />
            </div>

            <div className="auth-split__welcome auth-split__welcome--top">
              <h1>
                Let&apos;s get you
                <br />
                <span>back in</span>
              </h1>
              <p>
                Reset your password in three quick steps. Your conversations and
                settings stay exactly where you left them.
              </p>

              <ol className="otp-steps otp-steps--light">
                {STEPS.map((item, index) => (
                  <li
                    key={item.id}
                    className={
                      index < stepIndex
                        ? "is-done"
                        : index === stepIndex
                          ? "is-current"
                          : ""
                    }
                  >
                    <span className="otp-steps__marker" aria-hidden="true" />
                    {item.label}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="auth-mascot">
            <div className="auth-mascot__stage">
              <img
                src="/assets/Robot-Mascot.png"
                alt=""
                className="img-fluid robotImage"
              />

              <div className="auth-mascot__bubble">
                <Sparkles size={16} strokeWidth={2.4} />
                <span>
                  Happens to everyone.
                  <strong>Let&apos;s fix it.</strong>
                </span>
              </div>
            </div>
          </div>
          </div>
        </div>

        <div className="auth-split__form">
          <div className="auth-split__form-inner">
            {step === "email" && (
              <>
                <span className="otp-icon">
                  <MailIcon />
                </span>

                <div className="auth-split__form-header otp-header">
                  <h2>Forgot your password?</h2>
                  <p>
                    Enter the email you signed up with and we&apos;ll send you a{" "}
                    {OTP_LENGTH}-digit reset code.
                  </p>
                </div>

                <form onSubmit={handleSendCode} noValidate>
                  {error && <p className="auth-error">{error}</p>}

                  <label className="auth-field auth-field--iconed">
                    <span>Work email</span>
                    <span className="auth-field__icon" aria-hidden="true">
                      <Mail size={18} strokeWidth={1.9} />
                    </span>
                    <input
                      type="email"
                      className="auth-plain-input"
                      placeholder="Enter your work email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      autoComplete="email"
                      autoFocus
                    />
                  </label>

                  <button
                    type="submit"
                    className="onboarding__btn onboarding__btn--primary otp-submit"
                    disabled={submitting}
                  >
                    {submitting ? (
                      "Sending code..."
                    ) : (
                      <>
                        Send reset code
                        <ArrowRight size={18} strokeWidth={2.4} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}

            {step === "code" && (
              <>
                <span className="otp-icon">
                  <MailIcon />
                </span>

                <div className="auth-split__form-header otp-header">
                  <h2>Enter the reset code</h2>
                  <p>
                    We sent a {OTP_LENGTH}-digit code to <strong>{email}</strong>.
                  </p>
                </div>

                <form onSubmit={handleVerifyCode} noValidate>
                  {error && <p className="auth-error">{error}</p>}
                  {resent && !error && (
                    <p className="auth-success">
                      A new code is on its way to your inbox.
                    </p>
                  )}

                  <OtpInput
                    length={OTP_LENGTH}
                    onChange={handleCodeChange}
                    resetSignal={resetSignal}
                    disabled={submitting}
                  />

                  <button
                    type="submit"
                    className="onboarding__btn onboarding__btn--primary otp-submit"
                    disabled={code.length !== OTP_LENGTH || submitting}
                  >
                    {submitting ? (
                      "Verifying..."
                    ) : (
                      <>
                        Verify code
                        <ArrowRight size={18} strokeWidth={2.4} />
                      </>
                    )}
                  </button>
                </form>

                <p className="otp-resend">
                  Didn&apos;t get the code?{" "}
                  {secondsLeft > 0 ? (
                    <span className="otp-resend__timer">
                      Resend in {secondsLeft}s
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="auth-inline-link"  
                      onClick={handleResend}
                    >
                      Resend code
                    </button>
                  )}
                </p>

                <button type="button" className="otp-back" onClick={goBackToEmail}>
                  Wrong email? Go back
                </button>
              </>
            )}

            {step === "password" && (
              <>
                <span className="otp-icon">
                  <LockIcon />
                </span>

                <div className="auth-split__form-header otp-header">
                  <h2>Set a new password</h2>
                  <p>
                    Choose a password you haven&apos;t used before. It needs at least{" "}
                    {MIN_PASSWORD} characters.
                  </p>
                </div>

                <form onSubmit={handleResetPassword} noValidate>
                  {error && <p className="auth-error">{error}</p>}

                  <label className="auth-field auth-field--iconed">
                    <span>New password</span>
                    <span className="auth-field__icon" aria-hidden="true">
                      <Lock size={18} strokeWidth={1.9} />
                    </span>
                    <span className="auth-plain-input-wrap">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter a new password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setError("");
                        }}
                        autoComplete="new-password"
                        autoFocus
                      />
                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                    </span>
                  </label>

                  <label className="auth-field auth-field--iconed">
                    <span>Confirm new password</span>
                    <span className="auth-field__icon" aria-hidden="true">
                      <Lock size={18} strokeWidth={1.9} />
                    </span>
                    <span className="auth-plain-input-wrap">
                      <input
                        type={showConfirm ? "text" : "password"}
                        placeholder="Re-enter your new password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setError("");
                        }}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() => setShowConfirm((v) => !v)}
                        aria-label={showConfirm ? "Hide password" : "Show password"}
                      >
                        {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                    </span>
                  </label>

                  <button
                    type="submit"
                    className="onboarding__btn onboarding__btn--primary otp-submit"
                    disabled={submitting}
                  >
                    {submitting ? (
                      "Saving..."
                    ) : (
                      <>
                        Reset password
                        <ArrowRight size={18} strokeWidth={2.4} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}

            {/* On every step: someone who lands here by mistake, or who
                remembers the password halfway through, has a way out that is
                not the browser's back button. */}
            <p className="auth-back-line">
              Remembered your password?{" "}
              <button
                type="button"
                className="auth-inline-link"
                onClick={() => navigate(RoutesPaths.LOGIN)}
              >
                Back to sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
